import { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { DraftExercise, DraftSet, ExerciseMode } from '../../lib/workoutSession';
import {
  emptySet,
  findLastExercisePerformance,
  formatSetPrevious,
  hapticLight,
  isBodyweightHint,
} from '../../lib/workoutSession';
import type { Workout } from '../../types';

const pill =
  'min-w-0 rounded-xl border border-white/10 bg-white/[0.06] px-2.5 py-2 text-center text-sm font-semibold tabular-nums text-[var(--text-primary)] outline-none focus:border-sky-500/40';

const modeLabels: Record<ExerciseMode, string> = {
  weighted: 'Weighted',
  bodyweight: 'Bodyweight',
  timed: 'Timed',
};

export function ExerciseSessionCard({
  exercise,
  index,
  imperial,
  workouts,
  showAdvancedDefault,
  showRpe = true,
  showRir = true,
  onChange,
  onRemove,
  onSetComplete,
}: {
  exercise: DraftExercise;
  index: number;
  imperial: boolean;
  workouts: Workout[];
  showAdvancedDefault: boolean;
  showRpe?: boolean;
  showRir?: boolean;
  onChange: (next: DraftExercise) => void;
  onRemove: () => void;
  onSetComplete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const last = findLastExercisePerformance(workouts, exercise.name, imperial);
  const unit = imperial ? 'lb' : 'kg';
  const showAdvanced = (exercise.showAdvanced || showAdvancedDefault) && (showRpe || showRir);

  function updateSet(setKey: string, patch: Partial<DraftSet>) {
    onChange({
      ...exercise,
      sets: exercise.sets.map((s) => (s.key === setKey ? { ...s, ...patch } : s)),
    });
  }

  function setMode(mode: ExerciseMode) {
    hapticLight();
    onChange({
      ...exercise,
      mode,
      sets: exercise.sets.map((s) => ({
        ...s,
        bodyweight: mode === 'bodyweight',
        reps: mode === 'timed' ? '' : s.reps || '10',
        seconds: mode === 'timed' ? s.seconds || '45' : '',
      })),
    });
  }

  function toggleComplete(setKey: string, set: DraftSet) {
    const next = !set.completed;
    updateSet(setKey, { completed: next });
    if (next) {
      hapticLight();
      onSetComplete();
    }
  }

  function stepWeight(setKey: string, set: DraftSet, delta: number) {
    const n = Number(set.weight) || 0;
    const step = imperial ? 5 : 2.5;
    updateSet(setKey, { weight: String(Math.max(0, Math.round((n + delta * step) * 10) / 10)) });
  }

  function stepReps(setKey: string, set: DraftSet, delta: number) {
    const n = Math.max(0, Math.round(Number(set.reps) || 0));
    updateSet(setKey, { reps: String(Math.max(0, n + delta)) });
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-2xl border border-white/8 bg-[#121a2e] shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
    >
      <div className="flex items-start gap-2 border-b border-white/6 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-sky-400/70">Exercise {index + 1}</p>
          <h3 className="truncate font-display text-lg font-bold text-[var(--text-primary)]">{exercise.name}</h3>
          {last ? (
            <p className="mt-0.5 text-xs text-[var(--text-muted)]">
              Last: {formatSetPrevious(last, 0, imperial)}
            </p>
          ) : null}
        </div>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="rounded-lg px-2 py-1 text-lg text-[var(--text-muted)] hover:bg-white/[0.06]"
            aria-label="Exercise options"
          >
            ⋯
          </button>
          {menuOpen ? (
            <div className="absolute right-0 top-full z-10 mt-1 min-w-[140px] overflow-hidden rounded-xl border border-white/10 bg-[#151f33] py-1 shadow-xl">
              {showRpe || showRir ? (
              <button
                type="button"
                onClick={() => {
                  onChange({ ...exercise, showAdvanced: !exercise.showAdvanced });
                  setMenuOpen(false);
                }}
                className="block w-full px-3 py-2 text-left text-xs text-[var(--text-secondary)] hover:bg-white/[0.06]"
              >
                {exercise.showAdvanced ? 'Hide' : 'More'} metrics
              </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  onRemove();
                  setMenuOpen(false);
                }}
                className="block w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-500/10"
              >
                Remove exercise
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex gap-1.5 px-4 py-2.5">
        {(['weighted', 'bodyweight', 'timed'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
              exercise.mode === m
                ? 'bg-sky-500/20 text-sky-200 ring-1 ring-sky-500/30'
                : 'bg-white/[0.04] text-[var(--text-muted)]'
            }`}
          >
            {modeLabels[m]}
          </button>
        ))}
      </div>

      <div className="space-y-2 px-4 pb-3">
        <AnimatePresence initial={false}>
          {exercise.sets.map((set, si) => (
            <motion.div
              key={set.key}
              layout
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`rounded-xl px-2 py-2 ${set.completed ? 'bg-emerald-500/8 ring-1 ring-emerald-500/20' : 'bg-white/[0.03]'}`}
            >
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-xs font-bold text-[var(--text-muted)]">
                  {si + 1}
                </span>

                {exercise.mode === 'timed' ? (
                  <input
                    inputMode="numeric"
                    value={set.seconds}
                    onChange={(e) => updateSet(set.key, { seconds: e.target.value })}
                    placeholder="sec"
                    className={`${pill} flex-1`}
                  />
                ) : (
                  <>
                    {exercise.mode === 'weighted' ? (
                      <div className="flex min-w-0 flex-1 items-center gap-1">
                        <button type="button" onClick={() => stepWeight(set.key, set, -1)} className="rounded-lg bg-white/[0.06] px-2 py-2 text-xs text-[var(--text-muted)]">
                          −
                        </button>
                        <input
                          inputMode="decimal"
                          value={set.bodyweight ? '' : set.weight}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (isBodyweightHint(v)) updateSet(set.key, { bodyweight: true, weight: '' });
                            else updateSet(set.key, { weight: v, bodyweight: false });
                          }}
                          placeholder={set.bodyweight ? 'BW' : unit}
                          disabled={set.bodyweight}
                          className={`${pill} flex-1`}
                        />
                        <button type="button" onClick={() => stepWeight(set.key, set, 1)} className="rounded-lg bg-white/[0.06] px-2 py-2 text-xs text-[var(--text-muted)]">
                          +
                        </button>
                      </div>
                    ) : null}
                    <div className="flex min-w-0 flex-1 items-center gap-1">
                      <button type="button" onClick={() => stepReps(set.key, set, -1)} className="rounded-lg bg-white/[0.06] px-2 py-2 text-xs text-[var(--text-muted)]">
                        −
                      </button>
                      <input
                        inputMode="numeric"
                        value={set.reps}
                        onChange={(e) => updateSet(set.key, { reps: e.target.value })}
                        placeholder="reps"
                        className={`${pill} flex-1`}
                      />
                      <button type="button" onClick={() => stepReps(set.key, set, 1)} className="rounded-lg bg-white/[0.06] px-2 py-2 text-xs text-[var(--text-muted)]">
                        +
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => toggleComplete(set.key, set)}
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold transition ${
                    set.completed
                      ? 'bg-emerald-500 text-white shadow-[0_0_16px_rgba(16,185,129,0.4)]'
                      : 'border border-white/15 bg-white/[0.04] text-[var(--text-muted)]'
                  }`}
                  aria-label={set.completed ? 'Set complete' : 'Mark set complete'}
                >
                  ✓
                </button>
              </div>

              {showAdvanced ? (
                <div className="mt-2 flex gap-2 pl-10">
                  {showRpe ? (
                  <input
                    inputMode="decimal"
                    value={set.rpe}
                    onChange={(e) => updateSet(set.key, { rpe: e.target.value })}
                    placeholder="RPE"
                    className={`${pill} w-16`}
                  />
                  ) : null}
                  {showRir ? (
                  <input
                    inputMode="numeric"
                    value={set.rir}
                    onChange={(e) => updateSet(set.key, { rir: e.target.value })}
                    placeholder="RIR"
                    className={`${pill} w-16`}
                  />
                  ) : null}
                </div>
              ) : null}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <button
        type="button"
        onClick={() => {
          hapticLight();
          onChange({
            ...exercise,
            sets: [...exercise.sets, emptySet(exercise.mode, last ? {
              weight: last.weight,
              reps: last.reps,
              seconds: last.seconds,
              rpe: last.rpe,
              bodyweight: exercise.mode === 'bodyweight',
            } : undefined)],
          });
        }}
        className="mx-4 mb-4 w-[calc(100%-2rem)] rounded-xl border border-dashed border-white/12 py-2.5 text-sm font-semibold text-sky-300/90 hover:bg-sky-500/8"
      >
        + Add set
      </button>
    </motion.div>
  );
}
